import React, { useState, useEffect, useCallback } from 'react';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useData } from '../../contexts/DataContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Package, TrendingUp, DollarSign, FileText, Users,
  Calendar, BarChart3, RefreshCw, Wrench
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ReceiptsDashboardPage = () => {
  const { selectedPlant } = useData();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  useRealtimeEvent('receipt_created', () => fetchStats());
  useRealtimeEvent('stock_update', () => fetchStats());

  useEffect(() => {
    fetchStats();
  }, [period, selectedPlant]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = { days: parseInt(period) };
      if (selectedPlant) params.plant_id = selectedPlant;
      
      const response = await api.get('/wms/receipts/stats/dashboard', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Eroare la incarcare statistici');
    } finally {
      setLoading(false);
    }
  }, [period, selectedPlant]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Dashboard Receptii</h1>
          <p className="text-base text-slate-600 mt-1">Statistici si analiza receptii marfa</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/wms/stock-adjustment">
            <Button 
              className="h-12 bg-amber-600 hover:bg-amber-700 gap-2"
              data-testid="stock-adjust-btn"
            >
              <Wrench className="w-5 h-5" />
              <span className="hidden sm:inline">Reglare Stoc</span>
            </Button>
          </Link>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 h-12">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 zile</SelectItem>
              <SelectItem value="30">30 zile</SelectItem>
              <SelectItem value="90">90 zile</SelectItem>
              <SelectItem value="365">1 an</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchStats} className="h-12">
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Receptii</p>
                <p className="text-2xl font-bold">{stats?.total_receipts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Valoare Totala</p>
                <p className="text-2xl font-bold">{(stats?.total_value || 0).toLocaleString('ro-RO')} RON</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-xl">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Articole Receptionate</p>
                <p className="text-2xl font-bold">{stats?.total_items || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Furnizori Activi</p>
                <p className="text-2xl font-bold">{stats?.active_suppliers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Receptii pe Zile
          </h3>
          <div className="h-64">
            {stats?.daily_receipts?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.daily_receipts}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#3B82F6" name="Receptii" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="value" fill="#10B981" name="Valoare (RON)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                Nicio data disponibila pentru grafic
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top Articles & Recent Receipts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Top Articole Receptionate
            </h3>
            <div className="space-y-3">
              {stats?.top_articles?.map((article, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className="w-7 h-7 flex items-center justify-center bg-blue-600">{idx + 1}</Badge>
                    <span className="font-medium">{article.name}</span>
                  </div>
                  <span className="font-bold">{article.quantity} {article.unit || 'buc'}</span>
                </div>
              )) || <p className="text-slate-500 text-center py-8">Niciun articol</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Receptii Recente
            </h3>
            <div className="space-y-3">
              {stats?.recent_receipts?.map((receipt, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer">
                  <div>
                    <p className="font-semibold">{receipt.receipt_number}</p>
                    <p className="text-sm text-slate-600">{receipt.supplier_name} • {receipt.items_count} articole</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">{receipt.total_invoice_value?.toFixed(0)} {receipt.currency}</p>
                    <p className="text-xs text-slate-500">{new Date(receipt.created_at).toLocaleDateString('ro-RO')}</p>
                  </div>
                </div>
              )) || <p className="text-slate-500 text-center py-8">Nicio receptie recenta</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReceiptsDashboardPage;
